import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import {
  Print as PrintIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Devis, DevisStatus } from '../../types/devis';

interface DevisPreviewProps {
  devis: Devis;
  open: boolean;
  onClose: () => void;
  onPrint: () => void;
  onEdit: () => void;
}

const DevisPreview: React.FC<DevisPreviewProps> = ({
  devis,
  open,
  onClose,
  onPrint,
  onEdit,
}) => {
  const getStatusChip = (status: string) => {
    switch (status) {
      case DevisStatus.EN_COURS:
        return <Chip label="En cours" color="warning" />;
      case DevisStatus.VALIDE:
        return <Chip label="Validé" color="success" />;
      case DevisStatus.ANNULE:
        return <Chip label="Annulé" color="error" />;
      default:
        return <Chip label={status} />;
    }
  };

  const subtotal = devis.items?.reduce((sum, item) => sum + Number(item.total), 0) || 0;
  const discountAmount = subtotal * (Number(devis.remise) / 100);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Aperçu du Devis - {devis.reference}
          </Typography>
          {getStatusChip(devis.status)}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Header Info */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Informations du Devis
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Date:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {new Date(devis.date).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Référence:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {devis.reference}
                </Typography>
              </Box>
              {devis.demande_numero && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">N° Demande:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {devis.demande_numero}
                  </Typography>
                </Box>
              )}
              {devis.user_name && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Créé par:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {devis.user_name}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Client
              </Typography>
              <Typography variant="body1" fontWeight={500} gutterBottom>
                {devis.client_name}
              </Typography>
              {devis.client_ice && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">ICE:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {devis.client_ice}
                  </Typography>
                </Box>
              )}
              {devis.client_address && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Adresse:</Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right', maxWidth: '60%' }}>
                    {devis.client_address}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Line Items */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Articles
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.default' }}>
                    <TableCell>Désignation</TableCell>
                    <TableCell align="right">Quantité</TableCell>
                    <TableCell align="right">Prix Unitaire</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devis.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.designation}</TableCell>
                      <TableCell align="right">
                        {Number(item.quantite).toLocaleString('fr-FR')}
                      </TableCell>
                      <TableCell align="right">
                        {Number(item.prix_unitaire).toLocaleString('fr-FR', {
                          minimumFractionDigits: 2,
                        })}{' '}
                        DH
                      </TableCell>
                      <TableCell align="right">
                        {Number(item.total).toLocaleString('fr-FR', {
                          minimumFractionDigits: 2,
                        })}{' '}
                        DH
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Totals and Payment Info */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Conditions de Paiement
              </Typography>
              {devis.condition_paiement_nom && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Condition:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {devis.condition_paiement_nom}
                  </Typography>
                </Box>
              )}
              {devis.mode_paiement_nom && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Mode:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {devis.mode_paiement_nom}
                  </Typography>
                </Box>
              )}
              {devis.delai_livraison && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Délai de livraison:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {devis.delai_livraison} {devis.delai_livraison === 1 ? 'jour' : 'jours'}
                  </Typography>
                </Box>
              )}
              {devis.delai_devis_validation && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Validité du devis:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {devis.delai_devis_validation} {devis.delai_devis_validation === 1 ? 'jour' : 'jours'}
                  </Typography>
                </Box>
              )}
              {!devis.condition_paiement_nom && !devis.mode_paiement_nom && !devis.delai_livraison && !devis.delai_devis_validation && (
                <Typography variant="body2" color="textSecondary">
                  Non spécifié
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Totaux
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Sous-total:</Typography>
                <Typography variant="body2">
                  {subtotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Remise ({devis.remise}%):</Typography>
                <Typography variant="body2" color="error.main">
                  -{discountAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" fontWeight={600}>
                  Total:
                </Typography>
                <Typography variant="h6" fontWeight={600} color="#4CAF50">
                  {Number(devis.montant_total).toLocaleString('fr-FR', {
                    minimumFractionDigits: 2,
                  })}{' '}
                  DH
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Fermer
        </Button>
        <Button
          onClick={onPrint}
          startIcon={<PrintIcon />}
          variant="outlined"
          sx={{ borderColor: '#4CAF50', color: '#4CAF50' }}
        >
          Imprimer
        </Button>
        <Button
          onClick={onEdit}
          startIcon={<EditIcon />}
          variant="contained"
          sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#388E3C' } }}
        >
          Modifier
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DevisPreview;
